import { addDays } from "date-fns";

import Cookies from "universal-cookie";

import { StoreSlice } from "../types/StoreSlice";
import NitroService from "../utils/nitro.service";
import { Origin } from "../types/IDestinationOrigin";
import { IMultiLocationRoute } from "../types/IMultiLocationRoute";
import { IPinExtension } from "../types/ILocation";

const mapKey = "nitro.map1";
const zoomKey = "nitro.defaultZoom";

const cookies = new Cookies();

const getExpireTime = (days: number = 365) => {
  const expires = addDays(new Date(), days);
  return expires;
};

export type LabelType = "city" | "location" | "facility" | "prices";

export interface IMapSlice {
  zoom: number;
  setZoom: (zoom: number) => void;

  mapApiKey?: string;
  setMapApiKey: (mapApiKey: string) => void;

  mapType: "roadmap" | "satellite" | "hybrid" | "terrain";
  setMapType: (mapType: "roadmap" | "satellite" | "hybrid" | "terrain") => void;

  labelType?: LabelType;
  setLabelType: (labelType?: LabelType) => void;

  hoverLocation?: IPinExtension;
  setHoverLocation: (hoverLocation?: IPinExtension) => void;

  selectedLocation?: IPinExtension;
  setSelectedLocation: (selectedLocation?: IPinExtension) => void;

  selectedLocationLatLng?: { lat: number; lng: number };
  setSelectedLocationLatLng: (selectedLocationLatLng?: {
    lat: number;
    lng: number;
  }) => void;

  pinLocation?: { lat: number; lng: number };
  setPinLocation: (pinLocation?: { lat: number; lng: number }) => void;

  tempPinLocation?: { lat: number; lng: number };
  setTempPinLocation: (tempPinLocation?: { lat: number; lng: number }) => void;

  selectingPinLocation: boolean;
  setSelectingPinLocation: (selectingPinLocation: boolean) => void;

  // Will hide the location pins on the map
  //    Used when additing or editing a location/facility
  //    Used when showing competitive analysis routes
  hideLocationPins: boolean;
  setHideLocationPins: (hideLocationPins: boolean) => void;

  panToBounds?: google.maps.LatLngBounds;

  updatePanToBounds: (filters: any, locations: IPinExtension[]) => void;
  clearPanToBounds: () => void;

  panToCoordinates?: { lat: number; lng: number; zoom?: number };
  setPanToCoordinates: (panToCoordinates?: {
    lat: number;
    lng: number;
    zoom?: number;
  }) => void;

  multiLocationOrigins: Origin[];
  setMultiLocationOrigins: (origins: Origin[]) => void;

  multiLocationDestinations: IMultiLocationRoute[];
  setMultiLocationDestinations: (destinations: IMultiLocationRoute[]) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createMapSlice: StoreSlice<IMapSlice> = (set, get) => {
  const initialState = {
    zoom: cookies.get(zoomKey) || 5,
    mapApiKey: cookies.get(mapKey),
    mapType: "roadmap" as any,
    labelType: undefined,
    selectingPinLocation: false,
    hideLocationPins: false,
    multiLocationOrigins: [],
    multiLocationDestinations: [],
  };

  return {
    ...initialState,

    setMultiLocationOrigins: (multiLocationOrigins: Origin[]) => {
      set({ multiLocationOrigins });
    },

    setMultiLocationDestinations: (
      multiLocationDestinations: IMultiLocationRoute[],
    ) => {
      set({ multiLocationDestinations });
    },

    setZoom: (zoom: number) => {
      cookies.set(zoomKey, zoom, { expires: getExpireTime(365) });

      set({ zoom });
    },

    setMapApiKey: (mapApiKey: string) => {
      cookies.set(mapKey, mapApiKey, { expires: getExpireTime(365) });

      set((state) => ({ ...state, mapApiKey }));
    },

    setMapType: (mapType: "roadmap" | "satellite" | "hybrid" | "terrain") => {
      set({ mapType });
    },

    setLabelType: (labelType?: LabelType) => {
      set({ labelType });
    },

    setHoverLocation: (hoverLocation?: IPinExtension) => {
      set({ hoverLocation });
    },

    setSelectedLocation: (selectedLocation?: IPinExtension) => {
      set({
        selectedLocation,
        showAddLocationPanel: false,
        showAddFOBIndicationPanel: false,
      } as any);
    },

    setSelectedLocationLatLng: (selectedLocationLatLng?: {
      lat: number;
      lng: number;
    }) => {
      set({ selectedLocationLatLng });
    },

    setPinLocation: (pinLocation?: { lat: number; lng: number }) => {
      set({ pinLocation });
    },

    setTempPinLocation: (tempPinLocation?: { lat: number; lng: number }) => {
      set({ tempPinLocation });
    },

    setSelectingPinLocation: (selectingPinLocation: boolean) => {
      if (!selectingPinLocation) {
        set({
          selectingPinLocation,
        });
      }

      set({
        selectingPinLocation,
      });
    },

    setHideLocationPins: (hideLocationPins: boolean) => {
      set({ hideLocationPins });
    },

    updatePanToBounds: (filters: any, locations: IPinExtension[]) => {
      const { city, state, location, party } = filters;

      const panToState = async (stateName: string) => {
        try {
          const coords = await NitroService.getStateCoordinates(stateName);

          const latLngBounds = new google.maps.LatLngBounds();

          latLngBounds.extend(
            new google.maps.LatLng({
              lat: coords[0].yMin,
              lng: coords[0].xMin,
            }),
          );
          latLngBounds.extend(
            new google.maps.LatLng({
              lat: coords[0].yMax,
              lng: coords[0].xMax,
            }),
          );
          latLngBounds.extend(
            new google.maps.LatLng({
              lat: coords[0].yMin,
              lng: coords[0].xMax,
            }),
          );
          latLngBounds.extend(
            new google.maps.LatLng({
              lat: coords[0].yMax,
              lng: coords[0].xMin,
            }),
          );

          set({
            panToBounds: latLngBounds,
            panToCoordinates: undefined,
          });
        } catch (err) {
          console.error(err);
        }
      };

      // "all" Param --- If a facility is selected, there are no zip code pins to zoom too
      const panToLocations = (all?: boolean) => {
        // Zoom to zip codes only (excludes other pins not in city bounds)
        const pins = locations.filter((r: any) => {
          if (all) return true;

          return r.pinType === "zipCodes";
        });

        if (pins && pins.length > 0) {
          const latLngBounds = new google.maps.LatLngBounds();

          pins.forEach((b: any) => {
            latLngBounds.extend(
              new google.maps.LatLng({ lat: b.lat, lng: b.long }),
            );
          });

          set({
            panToBounds: latLngBounds,
            panToCoordinates: undefined,
          });
        }
      };

      if (city) {
        console.info("Zoom - city");
        panToLocations(false);
      } else if (location) {
        console.info("Zoom - location");
        panToLocations(true);
      } else if (state) {
        console.info("Zoom - state");
        panToState(state);
      } else if (party) {
        console.info("Zoom - party");
        panToLocations(true);
      } else {
        console.info("Zoom - default");

        set({
          panToBounds: undefined,
          panToCoordinates: {
            lat: 40,
            lng: -98,
          },
        });
      }
    },

    clearPanToBounds: () => {
      set({
        panToBounds: undefined,
      });
    },

    setPanToCoordinates: (panToCoordinates?: {
      lat: number;
      lng: number;
      zoom?: number;
    }) => {
      set({ panToCoordinates, pinLocation: undefined });
    },
  };
};
